import React, { memo, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup/dist/yup';

import { Button, Input, Select, SpinnerOverlay, TextArea, Tooltip } from '@components/common';

import { useQueryPortalProjectsAdmins } from '@hooks/portal/projects/portalProjects/useQueryPortalProjectsAdmins';
import { useQueryPortalProjectsMembers } from '@hooks/portal/projects/portalProjects/useQueryPortalProjectsMembers';
import { useQueryPortalProjectStatuses } from '@hooks/portal/projects/portalProjects/useQueryPortalProjectsStatuses';

import { auth } from '@store/selectors';
import { useAppSelector } from '@store/store';

import { projectUsersFormatted } from '@utils/formatter';

import { TNameTitleValueLabel } from '@interfaces/common/common';
import { IProjectFormValues } from '@interfaces/components/form';
import { IProjectDTO } from '@interfaces/portal/dto/projectManagementDTO';

import { ERevalidationMode, EValidationMode } from '@enums/validationMode';

import { regExpLetters } from '@constants/regExp';

import { getSchema } from './schema';

import styles from './EditPortalProjectForm.module.scss';

interface IProjectFormProps {
	toggleCloseConfirmModal: () => void;
	organisationId: string;
	submitForm: (values: IProjectFormValues) => void;
	editableProject: IProjectDTO;
	setIsDirtyFormModal: React.Dispatch<React.SetStateAction<boolean>>;
}

const EditPortalProjectForm = ({
	toggleCloseConfirmModal,
	organisationId,
	submitForm,
	editableProject,
	setIsDirtyFormModal,
}: IProjectFormProps) => {
	const { user } = useAppSelector(auth);

	const userId = user?.userId;

	const { portalProjectsStatuses, isLoadingPortalProjectsStatuses } = useQueryPortalProjectStatuses();
	const { portalProjectsAdmins, isLoadingPortalProjectsAdmins } = useQueryPortalProjectsAdmins(organisationId);
	const { portalProjectsMembers, isLoadingPortalProjectsMembers } = useQueryPortalProjectsMembers(organisationId);

	const isLoading = isLoadingPortalProjectsStatuses
		|| isLoadingPortalProjectsAdmins
		|| isLoadingPortalProjectsMembers;

	const preventSelfDeletion = (users: TNameTitleValueLabel[]) => {
		return users.map(user => {
			if (user.name === userId) {
				return {
					...user,
					disabled: true,
				};
			}

			return user;
		});
	};

	const {
		register,
		handleSubmit,
		formState: { errors, isDirty },
		setValue,
		watch,
		control,
	} = useForm<IProjectFormValues>({
		resolver: yupResolver(getSchema()),
		mode: EValidationMode.onSubmit,
		reValidateMode: ERevalidationMode.onChange,
		defaultValues: {
			projectName: editableProject?.name || '',
			projectCode: editableProject?.code || '',
			description: editableProject?.description || '',
			status: editableProject?.status && {
				...editableProject.status,
				value: editableProject.status.name,
				label: editableProject.status.title,
			},
			workflow: editableProject?.workflow && {
				...editableProject.workflow,
				value: editableProject.workflow.name,
				label: `${editableProject.workflow.title} (${editableProject.workflow.statuses})`,
			},
			admins: preventSelfDeletion(projectUsersFormatted(editableProject?.admins)),
			members: preventSelfDeletion(projectUsersFormatted(editableProject?.members)),
		},
	});

	const onChangeAdmins = (options: TNameTitleValueLabel[]) => setValue('admins', options);

	const onChangeMembers = (options: TNameTitleValueLabel[]) => setValue('members', options);

	const adminsValue = watch('admins');
	const membersValue = watch('members');
	const workflow = watch('workflow');

	const onSubmit = (values: IProjectFormValues) => submitForm(values);

	useEffect(() => setIsDirtyFormModal(isDirty), [isDirty, setIsDirtyFormModal]);

	return (
		<div className={styles.projectFormContainer}>
			{isLoading && <SpinnerOverlay />}
			<div className={styles.projectName}>
				<Input
					errorMessage={errors?.projectName?.message}
					label='Project Name'
					name='projectName'
					placeholder='Enter name'
					register={register}
					required
					setValue={setValue}
				/>
			</div>
			<div className={styles.projectCode}>
				<Input
					disabled
					errorMessage={errors?.projectCode?.message}
					label='Project Code'
					name='projectCode'
					placeholder='Enter code'
					regexValidation={regExpLetters}
					register={register}
					required
					setValue={setValue}
				/>
			</div>
			<div className={styles.status}>
				<Select
					control={control}
					errorMessage={errors.status?.message}
					isError={!!errors?.status}
					label='Status'
					name='status'
					options={portalProjectsStatuses}
					placeholder='Choose status'
					required
				/>
			</div>
			<Tooltip
				content={`${workflow?.label}`}
				isDisabled={!workflow?.label}
				offset={[0, -10]}
				placement='bottom'
			>
				<div className={styles.workflow}>
					<Select
						control={control}
						disabled
						errorMessage={errors.workflow?.message}
						isError={!!errors?.workflow}
						label='Workflow'
						name='workflow'
						placeholder='Choose workflow'
						required
					/>
				</div>
			</Tooltip>
			<div className={styles.projectDescription}>
				<TextArea
					errorMessage={errors.description?.message}
					isNotResizable
					label='Project Description'
					name='description'
					placeholder='Enter description'
					register={register}
					withUseForms
				/>
			</div>
			<div className={styles.admins}>
				<Select
					closeMenuOnSelect={false}
					control={control}
					handleChange={onChangeAdmins}
					isClearable={false}
					isError={!!errors.admins}
					isMulti
					label='Project Admins'
					multiselectText='admins selected'
					name='admins'
					options={preventSelfDeletion(portalProjectsAdmins)}
					placeholder='Choose admins'
					value={adminsValue}
				/>
			</div>
			<div className={styles.members}>
				<Select
					closeMenuOnSelect={false}
					control={control}
					handleChange={onChangeMembers}
					isClearable={false}
					isError={!!errors.members}
					isMulti
					label='Project Members'
					multiselectText='members selected'
					name='members'
					options={preventSelfDeletion(portalProjectsMembers)}
					placeholder='Choose members'
					value={membersValue}
				/>
			</div>
			<div className={styles.submitButtons}>
				<Button
					onClick={toggleCloseConfirmModal}
					text='Cancel'
					withoutBackground
				/>
				<Button
					onClick={handleSubmit(onSubmit)}
					text='Save'
				/>
			</div>
		</div>
	);
};

export default memo(EditPortalProjectForm);
